import {
  KeyboardEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Slider, Switch } from "@mui/material";
import { useMap } from "react-leaflet";
import { useLayers } from "../../context/LayersContext";
import { useRasterColormap } from "../../hooks/useRasterColormap";
import { useActiveRasterTypename } from "../../hooks/useActiveRasterTypename";
import { mockLayers } from "../../global/constants/layers";
import { Z } from "../../global/constants/zIndex";
import {
  RASTER_FILTER_META,
  RasterRangeOperator,
  RasterValueRange,
  stepForRange,
} from "../../global/constants/rasterValueFilters";
import { cn } from "../../lib/utils";

const OPERATOR_LABELS: Record<RasterRangeOperator, string> = {
  maior: "Maior que",
  menor: "Menor que",
  entre: "Entre",
};

const MAP_GESTURE_EVENTS = [
  "pointerdown",
  "pointerup",
  "pointercancel",
  "mousedown",
  "mouseup",
  "touchstart",
  "touchend",
  "touchcancel",
] as const;

const SLIDER_STOP_PROPAGATION_EVENTS = [
  "click",
  "dblclick",
  "contextmenu",
  "wheel",
] as const;

const PANEL_NATIVE_STOP_EVENTS = [
  "dblclick",
  "contextmenu",
  "wheel",
] as const;

const PANEL_LEAFLET_GESTURE_EVENTS = [
  "pointerdown",
  "mousedown",
  "touchstart",
] as const;

const APPLY_RANGE_DELAY_MS = 500;

type LeafletStoppedEvent = Event & { _stopped?: boolean };
type LeafletClickDisabledElement = HTMLElement & {
  _leaflet_disable_click?: boolean;
};

function fmt(value: number): string {
  return value.toLocaleString("pt-BR", { maximumFractionDigits: 1 });
}

/**
 * Filtro de exibição por faixa de valor do raster ativo (ex.: mostrar só GHI
 * acima de 5,8). Aparece para qualquer raster cujo SLD tenha uma rampa
 * contínua — os limites e as cores vêm do próprio colormap da camada.
 */
export function RasterRangeControl() {
  const map = useMap();
  const { layers, layerValueRanges, setLayerValueRange } = useLayers();
  const controlRef = useRef<HTMLDivElement | null>(null);
  const sliderRef = useRef<HTMLSpanElement | null>(null);
  const disabledMapDraggingRef = useRef(false);
  const sliderGestureDirtyRef = useRef(false);
  const applyRangeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // Só um raster fica ativo por vez (ver addNewLayer no LayersContext), então
  // o primeiro configurável encontrado é o que está na tela.
  const activeLayer = layers
    .map((title) => mockLayers.find((l) => l.title === title))
    .find((l) => l?.type === "raster");

  // Antes dos early returns: hook não pode ficar atrás de condicional.
  // A camada detalhada tem colormap próprio, e é ela que o RangeStyledWMSLayer
  // estiliza no zoom alto — ler os limites da base deixaria o controle
  // escrevendo limiares para outra camada.
  const activeTypename = useActiveRasterTypename(
    activeLayer?.layerName ?? "",
    activeLayer?.detailedLayerName,
    activeLayer?.detailedLayerZoomThreshold
  );
  const colormap = useRasterColormap(activeLayer?.workspace, activeTypename);

  // A faixa é guardada sob o nome base (é a chave que o renderizador consulta),
  // mas os limites vêm do typename ativo.
  const layerName = activeLayer?.layerName;
  const savedRange = layerName ? layerValueRanges[layerName] : undefined;
  const sliderMin = colormap ? colormap[0].quantity : undefined;
  const sliderMax = colormap
    ? colormap[colormap.length - 1].quantity
    : undefined;
  const meta = activeLayer
    ? RASTER_FILTER_META[activeLayer.layerName] ?? {}
    : {};

  const defaultRange = useMemo<RasterValueRange>(() => ({
    solidColor: false,
    operator: "maior",
    value: meta.reference?.value ?? sliderMin ?? 0,
    valueMax: sliderMax ?? sliderMin ?? 0,
  }), [meta.reference?.value, sliderMin, sliderMax]);

  const [draft, setDraft] = useState<RasterValueRange>(defaultRange);
  const draftRef = useRef<RasterValueRange>(defaultRange);

  const clearScheduledApply = useCallback(() => {
    if (applyRangeTimerRef.current === null) return;
    clearTimeout(applyRangeTimerRef.current);
    applyRangeTimerRef.current = null;
  }, []);

  const applyRange = useCallback((next: RasterValueRange) => {
    if (!activeLayer) return;
    clearScheduledApply();
    setLayerValueRange(activeLayer.layerName, next);
  }, [activeLayer, clearScheduledApply, setLayerValueRange]);

  const scheduleApplyRange = useCallback((next: RasterValueRange) => {
    if (!activeLayer) return;
    clearScheduledApply();
    applyRangeTimerRef.current = setTimeout(() => {
      applyRangeTimerRef.current = null;
      setLayerValueRange(activeLayer.layerName, next);
    }, APPLY_RANGE_DELAY_MS);
  }, [activeLayer, clearScheduledApply, setLayerValueRange]);

  // Trocar de typename pode estreitar o alcance e deixar a faixa já escolhida
  // fora dele — um limiar fora do colormap recorta tudo ou nada. Reencaixa nos
  // limites novos em vez de manter um filtro que a camada atual não representa.
  useEffect(() => {
    if (!layerName || !savedRange) return;
    if (sliderMin === undefined || sliderMax === undefined) return;
    const fit = (v: number) => Math.min(Math.max(v, sliderMin), sliderMax);
    const value = fit(savedRange.value);
    const valueMax = fit(savedRange.valueMax);
    if (value === savedRange.value && valueMax === savedRange.valueMax) return;
    setLayerValueRange(layerName, { ...savedRange, value, valueMax });
  }, [layerName, savedRange, sliderMin, sliderMax, setLayerValueRange]);

  useEffect(() => clearScheduledApply, [
    activeLayer?.layerName,
    clearScheduledApply,
  ]);

  useEffect(() => {
    if (!activeLayer || sliderMin === undefined || sliderMax === undefined) {
      draftRef.current = defaultRange;
      setDraft(defaultRange);
      return;
    }

    const next = savedRange ?? defaultRange;
    draftRef.current = next;
    setDraft((current) => {
      if (
        current.solidColor === next.solidColor &&
        current.operator === next.operator &&
        current.value === next.value &&
        current.valueMax === next.valueMax
      ) {
        return current;
      }
      return next;
    });
  }, [activeLayer, defaultRange, savedRange, sliderMin, sliderMax]);

  const beginSliderGesture = useCallback(() => {
    if (disabledMapDraggingRef.current) return;
    if (!map.dragging.enabled()) return;
    map.dragging.disable();
    disabledMapDraggingRef.current = true;
  }, [map.dragging]);

  const endSliderGesture = useCallback(() => {
    if (!disabledMapDraggingRef.current) return;
    map.dragging.enable();
    disabledMapDraggingRef.current = false;
    if (!sliderGestureDirtyRef.current) return;
    sliderGestureDirtyRef.current = false;
    scheduleApplyRange(draftRef.current);
  }, [map.dragging, scheduleApplyRange]);

  // O slider fica dentro do container do Leaflet. O gesto precisa chegar ao
  // MUI, então suspendemos o drag do mapa durante a interação em vez de
  // bloquear mousedown/touchstart no painel.
  useEffect(() => {
    const node = sliderRef.current;
    if (!node) return;

    const stopSliderClickPropagation = (event: Event) => {
      event.stopPropagation();
    };

    MAP_GESTURE_EVENTS.forEach((eventName) => {
      if (
        eventName === "pointerdown" ||
        eventName === "mousedown" ||
        eventName === "touchstart"
      ) {
        node.addEventListener(eventName, beginSliderGesture);
      } else {
        document.addEventListener(eventName, endSliderGesture);
      }
    });

    SLIDER_STOP_PROPAGATION_EVENTS.forEach((eventName) => {
      node.addEventListener(eventName, stopSliderClickPropagation);
    });

    return () => {
      endSliderGesture();
      MAP_GESTURE_EVENTS.forEach((eventName) => {
        if (
          eventName === "pointerdown" ||
          eventName === "mousedown" ||
          eventName === "touchstart"
        ) {
          node.removeEventListener(eventName, beginSliderGesture);
        } else {
          document.removeEventListener(eventName, endSliderGesture);
        }
      });
      SLIDER_STOP_PROPAGATION_EVENTS.forEach((eventName) => {
        node.removeEventListener(eventName, stopSliderClickPropagation);
      });
    };
  }, [beginSliderGesture, endSliderGesture]);

  useEffect(() => {
    const node = controlRef.current as LeafletClickDisabledElement | null;
    if (!node) return;

    // Do not call L.DomEvent.disableClickPropagation here: it also stops
    // mousedown/touchstart, which breaks the MUI Slider's delegated handlers.
    // Stop only the native events that still leak to Leaflet map handlers.
    node._leaflet_disable_click = true;

    const stopPanelLeafletEvent = (event: Event) => {
      (event as LeafletStoppedEvent)._stopped = true;
    };

    const stopPanelNativeEvent = (event: Event) => {
      stopPanelLeafletEvent(event);
      event.stopPropagation();
    };

    const stopPanelGesture = (event: Event) => {
      stopPanelLeafletEvent(event);
      beginSliderGesture();
    };

    PANEL_NATIVE_STOP_EVENTS.forEach((eventName) => {
      node.addEventListener(eventName, stopPanelNativeEvent);
    });

    PANEL_LEAFLET_GESTURE_EVENTS.forEach((eventName) => {
      node.addEventListener(eventName, stopPanelGesture);
    });

    return () => {
      delete node._leaflet_disable_click;
      PANEL_NATIVE_STOP_EVENTS.forEach((eventName) => {
        node.removeEventListener(eventName, stopPanelNativeEvent);
      });
      PANEL_LEAFLET_GESTURE_EVENTS.forEach((eventName) => {
        node.removeEventListener(eventName, stopPanelGesture);
      });
    };
  }, [beginSliderGesture]);

  // Sem rampa contínua não há faixa de valores para oferecer.
  if (
    !activeLayer ||
    !colormap ||
    sliderMin === undefined ||
    sliderMax === undefined
  )
    return null;

  const range = savedRange;
  const step = stepForRange(sliderMin, sliderMax);

  const updateDraft = (patch: Partial<RasterValueRange>) => {
    const next = { ...draft, ...patch };
    draftRef.current = next;
    setDraft(next);
    return next;
  };

  const updateAndApply = (patch: Partial<RasterValueRange>) => {
    const next = updateDraft(patch);
    applyRange(next);
  };

  const updateAndScheduleApply = (patch: Partial<RasterValueRange>) => {
    const next = updateDraft(patch);
    scheduleApplyRange(next);
  };

  // Campo vazio ou fora do colormap viraria um limite sem sentido para a
  // camada (0 num GHI que vai de 4 a 6 mostra o raster inteiro).
  const clamp = (raw: string, fallback: number) => {
    const parsed = parseFloat(raw);
    if (Number.isNaN(parsed)) return fallback;
    return Math.min(Math.max(parsed, sliderMin), sliderMax);
  };

  const stopMapPropagation = (event: SyntheticEvent) => {
    event.stopPropagation();
  };

  const stopMapGesture = (event: SyntheticEvent) => {
    event.stopPropagation();
    beginSliderGesture();
  };

  const commitDraftOnEnter = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    applyRange(draftRef.current);
  };

  return (
    // Canto esquerdo: o direito é do DimensionControl, e a camada de vento
    // acende os dois controles ao mesmo tempo.
    <div
      ref={controlRef}
      data-testid="raster-range-control"
      style={{ zIndex: Z.MAP_OVERLAY_CONTROL }}
      onPointerDown={stopMapGesture}
      onMouseDown={stopMapGesture}
      onTouchStart={stopMapGesture}
      onClick={stopMapPropagation}
      onDoubleClick={stopMapPropagation}
      onContextMenu={stopMapPropagation}
      onWheel={stopMapPropagation}
      className="absolute bottom-5 left-5 w-[260px] rounded-lg bg-white/95 p-4 shadow-md"
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <h3 className="text-sm font-semibold text-primary">
          {activeLayer.title}
        </h3>
        {range && (
          <button
            type="button"
            onClick={() => {
              clearScheduledApply();
              setLayerValueRange(activeLayer.layerName, null);
            }}
            className="shrink-0 border-none bg-transparent p-0 text-xs text-gray-400 hover:text-red-400"
          >
            Limpar
          </button>
        )}
      </div>

      {/* O recorte é o mesmo nos dois lados do switch: muda só a cor — o
          gradiente original (SLD dinâmico no GeoServer) ou a máscara em cor
          sólida (tiles do backend). */}
      {/* Sem tileKey o backend não serve essa camada recortada, então só o
          caminho do gradiente existe e o switch não teria para onde alternar. */}
      {meta.tileKey && (
        <label className="mb-1 flex cursor-pointer items-center justify-between gap-2">
          <span className="text-xs text-gray-700">
            {draft.solidColor ? "Cor sólida" : "Gradiente original"}
          </span>
          <Switch
            size="small"
            checked={draft.solidColor}
            onChange={(_event, checked) =>
              updateAndApply({ solidColor: checked })
            }
            inputProps={{ "aria-label": "Pintar o recorte em cor sólida" }}
            sx={{ color: "var(--primary)" }}
          />
        </label>
      )}

      {draft.solidColor && meta.tileNote && (
        <p className="mb-1 text-xs text-amber-600">{meta.tileNote}</p>
      )}

      <p className="mb-2 text-xs text-gray-500">
        {range
          ? draft.solidColor
            ? "Recorte em cor sólida, para ler a máscara de dados."
            : "Recorte com as cores originais do raster."
          : "Exibindo o raster inteiro. Escolha uma faixa para filtrar."}
      </p>

      <select
        value={draft.operator}
        onChange={(e) =>
          updateAndApply({ operator: e.target.value as RasterRangeOperator })
        }
        className="w-full rounded border border-gray-200 px-2 py-1.5 text-xs text-gray-700"
      >
        {(
          Object.entries(OPERATOR_LABELS) as [RasterRangeOperator, string][]
        ).map(([value, label]) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </select>

      <div className="mt-2 flex items-center gap-2">
        <input
          type="number"
          step={step}
          value={draft.value}
          onChange={(e) =>
            updateAndScheduleApply({
              value: clamp(e.target.value, sliderMin),
            })
          }
          onBlur={() => applyRange(draftRef.current)}
          onKeyDown={commitDraftOnEnter}
          className="w-20 rounded border border-gray-200 px-1.5 py-1 text-right text-xs"
        />
        {draft.operator === "entre" && (
          <>
            <span className="text-xs text-gray-500">e</span>
            <input
              type="number"
              step={step}
              value={draft.valueMax}
              onChange={(e) =>
                updateAndScheduleApply({
                  valueMax: clamp(e.target.value, sliderMax),
                })
              }
              onBlur={() => applyRange(draftRef.current)}
              onKeyDown={commitDraftOnEnter}
              className="w-20 rounded border border-gray-200 px-1.5 py-1 text-right text-xs"
            />
          </>
        )}
        {meta.unit && (
          <span className="text-xs text-gray-400">{meta.unit}</span>
        )}
      </div>

      {/* Um controle só: em "entre" ele ganha os dois cursores (mínimo e
          máximo) no mesmo trilho. `disableSwap` impede que arrastar um cursor
          além do outro inverta a faixa. */}
      <div className="mt-3 px-1">
        <Slider
          ref={sliderRef}
          data-testid="raster-range-slider"
          size="small"
          min={sliderMin}
          max={sliderMax}
          step={step}
          disableSwap
          value={
            draft.operator === "entre"
              ? [draft.value, draft.valueMax]
              : draft.value
          }
          onChange={(_event, next) => {
            sliderGestureDirtyRef.current = true;
            if (Array.isArray(next)) {
              updateDraft({ value: next[0], valueMax: next[1] });
            } else {
              updateDraft({ value: next });
            }
          }}
          onChangeCommitted={(_event, next) => {
            if (Array.isArray(next)) {
              updateAndScheduleApply({ value: next[0], valueMax: next[1] });
            } else {
              updateAndScheduleApply({ value: next });
            }
          }}
          valueLabelDisplay="auto"
          valueLabelFormat={fmt}
          getAriaLabel={(index) =>
            index === 0 ? "Valor mínimo" : "Valor máximo"
          }
          aria-label={activeLayer.title}
          sx={{ color: "var(--primary)", py: 1 }}
        />
      </div>
      <div className="flex justify-between text-xs text-gray-400">
        <span>{fmt(sliderMin)}</span>
        <span>{fmt(sliderMax)}</span>
      </div>

      {draft.operator === "entre" && draft.value > draft.valueMax && (
        <p className="mt-1 text-xs text-red-400">
          O mínimo está acima do máximo — nenhum pixel será exibido.
        </p>
      )}

      {meta.reference && (
        <button
          type="button"
          onClick={() =>
            updateAndApply({ operator: "maior", value: meta.reference!.value })
          }
          className={cn(
            "mt-2 w-full rounded border border-gray-200 bg-transparent px-2 py-1 text-xs text-gray-500",
            "hover:border-primary hover:text-primary"
          )}
        >
          {meta.reference.hint}
        </button>
      )}
    </div>
  );
}

export default RasterRangeControl;
